import classnames from 'classnames';
import React from 'react';
import {
  sortMembersBySortOrderFirst,
  sortNodesBySortOrderFirst,
  sortRelatedGuidesBySortOrderFirst,
} from '../../../../../utils/applyMenuConfigChanges/addSortOrderProperty/sortBySortOrder';
import { TYPES } from '../../../../../utils/flattenTree/utils/consts';
import { isMainEntity } from '../../../../../utils/flattenTree/utils/extractors';
import { removeStartingSlash } from '../../../../../utils/locationUtils';
import { getName } from '../../../../../utils/treeVisitors/helpers';
import { ActiveLink } from '../../../ActiveLink';
import { connectStore } from '../../../../appContext/StoreProvider/ConnectStore';
import GuideLink from '../../../TreeMenu/GuideLink';
import {
  hasMainEntities,
  markMainEntity,
  shouldAddWebhook,
} from '../../../TreeMenu/utils';
import { TreeMenuLink } from '../../../TreeMenuLink/TreeMenuLink';
import { isSubTreeKey, sectionPrefix } from '../../utils';
import { SubTreeLink } from './SubTreeLink';
import style from './TreeNavigationMenu.scss';
import { getOpenMenuIdFromUrl } from './utils';
import { withExperiments } from '@wix/wix-experiments-react';

class _TreeNavigationMenu extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      openSubTreeId: '',
      enterAnimation: false,
    };
  }
  getMdGuidesLinksByTreeKey = () => {
    const { treeDepth, tree } = this.props;

    const currentTreeGuides = tree.data.guides || tree.data.relatedGuides;

    const sortedTreeGuides =
      sortRelatedGuidesBySortOrderFirst(currentTreeGuides);
    return sortedTreeGuides.map((guide, i) => (
      <GuideLink
        treeDepth={treeDepth}
        key={`MD__${guide.name}__${i}`}
        {...guide}
        prefix={this.props.prefix}
      />
    ));
  };

  shouldRenderSubTree = (id) => {
    return this.state.openSubTreeId === id;
  };

  toggleSubTree = (urlFormat) => (id) => {
    if (this.state.openSubTreeId === id) {
      urlFormat && this.props.markAsRecentlyClicked(urlFormat);
      this.setState({ openSubTreeId: '' });
      return;
    }
    if (!urlFormat && this.props.hasBeenClickedRecently()) {
      return;
    }

    this.setState({ openSubTreeId: id });
  };

  getSubTreeLinks = () => {
    const { tree } = this.props;
    const subTreesLinks = [];

    sortNodesBySortOrderFirst(tree).forEach((key) => {
      if (isSubTreeKey(key)) {
        const { urlFormat } = tree[key].data;
        const keyWithoutStartingSlash = removeStartingSlash(key);
        const name = getName(tree[key].data);
        subTreesLinks.push(
          <React.Fragment key={`${name}__frag`}>
            <SubTreeLink
              depth={this.props.treeDepth}
              key={`${name}__${urlFormat}`}
              shouldRenderSubTree={this.shouldRenderSubTree(urlFormat)}
              urlFormat={urlFormat}
              onClick={this.toggleSubTree(urlFormat)}
              name={name}
              prefix={this.props.prefix}
            />
            {this.shouldRenderSubTree(urlFormat) && (
              <TreeNavigationMenu
                {...this.props}
                key={`${key}_${name}__${urlFormat}`}
                treeDepth={this.props.treeDepth + 1}
                treeKey={`/${keyWithoutStartingSlash.split('/')[0]}`}
                tree={tree[key]}
                prefix={sectionPrefix(this.props.prefix, urlFormat)}
              />
            )}
          </React.Fragment>,
        );
      }
    });
    return subTreesLinks;
  };

  toTreeLink = (type, JSLayoutType) => (member) =>
    (
      <TreeMenuLink
        key={member.urlFormat}
        item={member}
        type={type}
        prefix={this.props.prefix}
        JSLayoutType={JSLayoutType}
      />
    );

  getEventLinks = (JSLayoutType) => {
    const { tree } = this.props;

    if (shouldAddWebhook(tree)) {
      return sortMembersBySortOrderFirst(tree.data.events).map(
        this.toTreeLink(TYPES.EVENT, JSLayoutType),
      );
    }
    return [];
  };

  getMainEntity = (JSLayoutType) => {
    const { tree } = this.props;
    if (!hasMainEntities(tree)) {
      return [];
    }
    markMainEntity(tree);
    return sortMembersBySortOrderFirst(tree.data.messages)
      .filter(isMainEntity)
      .map(this.toTreeLink(TYPES.MAIN_ENTITY, JSLayoutType));
  };

  getTreeMemberLinks = () => {
    const { tree } = this.props;
    let memberslinks = [];
    if (tree.data) {
      const JSLayoutType = tree?.data?.layoutType === 'JS';
      memberslinks = [
        ...this.getMainEntity(JSLayoutType),
        ...sortMembersBySortOrderFirst(tree.data.properties).map(
          this.toTreeLink(TYPES.PROPERTY, JSLayoutType),
        ),
        ...sortMembersBySortOrderFirst(tree.data.operations).map(
          this.toTreeLink(TYPES.OPERATION, JSLayoutType),
        ),
        ...sortMembersBySortOrderFirst(tree.data.clientEvents).map(
          this.toTreeLink(TYPES.CLIENT_EVENT, JSLayoutType),
        ),
        ...this.getEventLinks(JSLayoutType),
      ];
    }
    return memberslinks;
  };

  treeLinks = () => {
    const mdGuidesLinks = this.getMdGuidesLinksByTreeKey();
    const subTreesLinks = this.getSubTreeLinks();
    const treeMemberLinks = this.getTreeMemberLinks();
    return [...mdGuidesLinks, ...treeMemberLinks, ...subTreesLinks];
  };

  openSubTreeMenus = (link) => {
    if (link) {
      const openMenuId = getOpenMenuIdFromUrl(
        link,
        this.props.treeDepth,
        this.props.store,
      );
      if (openMenuId && this.state.openSubTreeId !== openMenuId) {
        this.toggleSubTree()(openMenuId);
      }
    }
  };

  componentDidMount() {
    if (this.props.treeDepth) {
      this.timeoutId = setTimeout(
        () =>
          this.setState({ enterAnimation: true }, () => {
            this.props.menuSync(
              `${window.location.pathname}${window.location.hash}`,
            );
          }),
        15,
      );
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timeoutId);
  }

  render() {
    const menuLinks = this.treeLinks();
    const classNames = classnames({
      [style.treeNavigationLinks]: true,
      [style[`depth-${this.props.treeDepth}`]]: true,
      [style.enterAnimation]: this.state.enterAnimation,
    });
    const { prefix } = this.props;
    return (
      <ActiveLink onActiveLinkChange={this.openSubTreeMenus}>
        <div
          key={prefix}
          className={classNames}
          data-hook={`tree-menu-depth-${this.props.treeDepth}`}
        >
          {menuLinks}
        </div>
      </ActiveLink>
    );
  }
}

export const TreeNavigationMenu = withExperiments(
  connectStore(_TreeNavigationMenu),
);
